import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Send, WandSparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";

const QUICK_PROMPTS = [
  "Suggest 5 taglines for my business",
  "Build me a color palette with hex codes",
  "Give me an LLC startup checklist",
  "Write my Instagram bio",
  "Help me price my services",
];

export function renderContent(text) {
  return text.split("\n").map((line, i) => {
    const isHeader = /^#{1,4}\s/.test(line);
    const clean = line.replace(/^#{1,4}\s*/, "").replace(/^\s*[-•]\s+/, "•  ");
    const parts = clean.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
      seg.startsWith("**") ? <strong key={j} className="text-[#3A2E25]">{seg.slice(2, -2)}</strong> : seg
    );
    return <p key={i} className={isHeader ? "font-bold text-[#A8785A] mt-1" : ""}>{parts}</p>;
  });
}

function ApplyButton({ content, onApply, qs }) {
  const [state, setState] = useState("idle");
  const [labels, setLabels] = useState([]);
  const run = async () => {
    setState("busy");
    try {
      const { data } = await api.post(`/ai/apply${qs}`, { answer: content });
      if (!data.applied.length) {
        setState("idle");
        toast("Nothing in that answer maps to a field yet — ask for something specific like a tagline or palette.");
        return;
      }
      onApply(data);
      setLabels(data.applied.map((a) => a.label));
      setState("done");
    } catch (e) {
      setState("idle");
      toast.error(formatApiError(e));
    }
  };
  if (state === "done") {
    return (
      <p className="mt-3 flex items-start gap-1.5 text-[11px] font-semibold text-[#A8785A]" data-testid="ai-applied-note">
        <Check size={13} className="mt-0.5 shrink-0" /> Applied to {labels.join(", ")}
      </p>
    );
  }
  return (
    <button onClick={run} disabled={state === "busy"} data-testid="ai-apply-btn"
      className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold rounded-full px-3.5 py-1.5 bg-[#A8785A] text-[#FFFDF7] hover:bg-[#3A2E25] transition-colors disabled:opacity-60">
      <WandSparkles size={12} /> {state === "busy" ? "Applying…" : "Apply to my organizer"}
    </button>
  );
}

export default function AIAssistant({ open, onClose, onApply, brandVoice, ideaId }) {
  const qs = ideaId ? `?idea=${ideaId}` : "";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && !loaded) {
      setLoaded(true);
      api.get(`/ai/history${qs}`).then(({ data }) => setMessages(data.messages || [])).catch(() => {});
    }
  }, [open, loaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API_BASE}/ai/chat${qs}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection hiccup — try that again." };
        return copy;
      });
    }
    setStreaming(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ x: 420 }} animate={{ x: 0 }} exit={{ x: 420 }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          className="fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[400px] glass border-l border-[#E8DCCB] flex flex-col shadow-[-12px_0_40px_rgba(58,46,37,0.08)]"
          data-testid="ai-assistant">
          <div className="flex items-center justify-between px-5 h-16 border-b border-[#E8DCCB] shrink-0 bg-[#FFF3F8]">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-[#FFFDF7] border border-[#D4A0B4] flex items-center justify-center">
                <Sparkles size={15} className="text-[#A8785A]" />
              </span>
              <div>
                <p className="f-display font-medium text-sm leading-none">Organizer Copilot</p>
                <p className="f-mono text-[10px] text-[#C48A9F] mt-1">{brandVoice ? "WRITING IN YOUR BRAND VOICE" : "ONLINE · CLAUDE"}</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="ai-close-btn" className="text-[#8C7562] hover:text-[#3A2E25] transition-colors">
              <X size={18} />
            </button>
          </div>

          {brandVoice ? (
            <p className="px-5 py-2.5 text-[11px] text-[#57473B] bg-[#FFFDF7] border-b border-[#E8DCCB] truncate" data-testid="ai-brand-voice-chip">
              <span className="font-semibold text-[#A8785A]">Voice:</span> {brandVoice}
            </p>
          ) : (
            <p className="px-5 py-2.5 text-[11px] text-[#8C7562] bg-[#FFFDF7] border-b border-[#E8DCCB]" data-testid="ai-brand-voice-hint">
              Tip: fill in <span className="font-semibold">Brand Voice</span> (Branding section) and every tagline and bio will match your tone.
            </p>
          )}
          <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-4" data-testid="ai-messages">
            {messages.length === 0 && (
              <div className="text-sm text-[#8C7562] leading-relaxed">
                I can see your whole organizer. Ask me to draft taglines, pick hex palettes, plan your LLC steps,
                write social bios, or price your offers — then tap “Apply to my organizer” to drop the answer into your fields.
              </div>
            )}
            {messages.map((m, i) => {
              const isLast = i === messages.length - 1;
              const complete = m.role === "assistant" && m.content && !(streaming && isLast);
              return (
                <div key={i}
                  className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "self-end bg-[#A8785A] text-[#FFFDF7] font-medium rounded-br-md"
                      : "self-start bg-[#FFFDF7] border border-[#E8DCCB] text-[#3A2E25] rounded-bl-md"
                  }`}>
                  {m.role === "assistant" ? renderContent(m.content) : m.content}
                  {m.role === "assistant" && streaming && isLast && (
                    <span className="inline-block w-1.5 h-3.5 bg-[#C48A9F] ml-1 animate-pulse" />
                  )}
                  {complete && onApply && <ApplyButton key={m.content.length} content={m.content} onApply={onApply} qs={qs} />}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <div className="px-5 pb-2 flex flex-wrap gap-2 shrink-0">
            {QUICK_PROMPTS.map((q) => (
              <button key={q} onClick={() => send(q)} data-testid="ai-quick-prompt"
                className="text-[11px] font-semibold border border-[#E8DCCB] rounded-full px-3 py-1.5 text-[#8C7562] hover:border-[#A8785A] hover:text-[#A8785A] transition-colors">
                {q}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-4 border-t border-[#E8DCCB] flex gap-2 shrink-0">
            <input className="field !py-2.5" placeholder="Ask the copilot…" value={input}
              onChange={(e) => setInput(e.target.value)} data-testid="ai-input" />
            <button type="submit" disabled={streaming} data-testid="ai-send-btn"
              className="w-11 h-11 rounded-xl bg-[#A8785A] text-[#FFFDF7] flex items-center justify-center shrink-0 hover:bg-[#3A2E25] transition-colors disabled:opacity-50">
              <Send size={16} />
            </button>
          </form>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
